import React from 'react';
import { TableCell, Chip, Tooltip, Button } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import { useTranslation } from 'react-i18next';
import { PackageVisit } from './package.helper';

interface ClosePackageCellProps {
  /** Package visit row data */
  row: PackageVisit | null | undefined;
  /** Handler when close package button is clicked - optional for safety */
  onClosePackage?: (e: React.MouseEvent, row: PackageVisit) => void;
  /** Whether the current user's clinic doesn't match the row's clinic */
  isClinicMismatch?: boolean;
}

/**
 * Reusable table cell component for closing packages.
 * 
 * Features:
 * - Displays "Package Closed" chip when package is already closed
 * - Shows "Close Package" button with dynamic tooltip messages based on status
 * - Handles all edge cases with proper fallbacks
 * 
 * Edge Cases Handled:
 * - Null/undefined row data → Shows "N/A" chip
 * - Missing onClosePackage handler → Button disabled with warning in dev mode
 * - Status variations (ToDo/todo/TO_DO) → Normalized to lowercase
 * - Missing translations → Fallback to English strings
 * - Event handler errors → Caught and logged without breaking UI
 * - Missing can_close_package_invoice → Treated as false (button disabled)
 * - Missing is_package_closed → Treated as false (shows button, not chip)
 * 
 * @param {ClosePackageCellProps} props - Component props
 * @param {PackageVisit} props.row - Package visit data (nullable for safety)
 * @param {Function} props.onClosePackage - Optional click handler for close button
 * @returns {JSX.Element} TableCell with close package UI
 */
const ClosePackageCellComponent: React.FC<ClosePackageCellProps> = ({ row, onClosePackage, isClinicMismatch = false }) => {
  const { t } = useTranslation('Packagebilling');

  // Defensive: Handle missing or invalid row data
  if (!row) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn('[ClosePackageCell] Row data is null or undefined');
    }
    return (
      <TableCell align="center">
        <Chip 
          label="N/A" 
          size="small" 
          sx={{ 
            backgroundColor: '#f5f5f5', 
            color: '#9e9e9e',
            height: '32px',
            fontSize: '13px',
          }} 
        />
      </TableCell>
    );
  }

  const getTooltipMessage = (): string => {
    // Check clinic mismatch first
    if (isClinicMismatch) {
      return t('close_package_clinic_mismatch_tooltip') || 'You can only close packages from your assigned clinic';
    }

    // Check if there's an active refund request
    if (row.is_active_package_invoice_refund_request) {
      return t('close_package_refund_request_tooltip') || 'Refund or Reject all services from refund request';
    }

    // Safely check if package can be closed
    const canClose = row.can_close_package_invoice === true;
    
    if (canClose) {
      return t('close_package_tooltip') || 'Close package and generate final invoice';
    }

    // Button is disabled - show status-specific message
    const status = row.status || '';
    const normalizedStatus = status.toLowerCase().replace(/[_\s]/g, '');
    
    // Normalize status comparison (handle different casings and formats)
    if (normalizedStatus === 'todo') {
      return t('close_package_todo_tooltip') || 'Please fill the package details';
    }
    if (normalizedStatus === 'inprogress') {
      return t('close_package_incomplete_tooltip') || 'Please save and finalize';
    }
    if (normalizedStatus === 'completed') {
      return t('close_package_completed_disabled_tooltip') || 'Package requirements not met. Please review details.';
    }
    
    // Default fallback
    return t('close_package_disabled_tooltip') || 'Cannot close package - pending bills must be settled first';
  };

  // Safe event handler wrapper
  const handleButtonClick = (e: React.MouseEvent) => {
    try {
      if (onClosePackage && typeof onClosePackage === 'function') {
        onClosePackage(e, row);
      } else if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn('[ClosePackageCell] onClosePackage handler is not provided or not a function');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[ClosePackageCell] Error in onClosePackage handler:', error);
    }
  };

  // Safely determine if package is closed
  const isPackageClosed = row.is_package_closed === true;
  const hasActiveRefundRequest = row.is_active_package_invoice_refund_request === true;
  const canClosePackage = row.can_close_package_invoice === true && !isClinicMismatch && !hasActiveRefundRequest;

  // Safe click handler for table cell
  const handleCellClick = (e: React.MouseEvent) => {
    try {
      if (!canClosePackage) {
        e.stopPropagation();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[ClosePackageCell] Error in cell click handler:', error);
    }
  };

  return (
    <TableCell 
      align="center"
      onClick={handleCellClick}
    >
      {isPackageClosed ? (
        <Chip
          icon={<CheckCircleIcon sx={{ fontSize: '18px !important' }} />}
          label={t('package_closed') || 'Package Closed'}
          size="small"
          sx={{
            backgroundColor: '#e8f5e9',
            color: '#2e7d32',
            border: '1px solid #a5d6a7',
            fontWeight: 500,
            height: '32px',
            fontSize: '13px',
            '& .MuiChip-icon': {
              color: '#2e7d32',
            },
          }}
        />
      ) : (
        <Tooltip 
          title={getTooltipMessage()}
          arrow
          placement="top"
          // Prevent tooltip errors
          disableInteractive={false}
        >
          <span>
            <Button
              variant="outlined"
              size="small"
              disabled={!canClosePackage}
              onClick={handleButtonClick}
              // Prevent button click when handler is missing
              aria-disabled={!onClosePackage}
              sx={{
                textTransform: 'none',
                borderRadius: '20px',
                borderColor: '#666',
                color: '#333',
                fontWeight: 500,
                fontSize: '13px',
                px: 2.5,
                py: 0.5,
                minWidth: '120px',
                height: '32px',
                '&:hover': {
                  borderColor: '#1565c0',
                  backgroundColor: '#e3f2fd',
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.2)',
                },
                '&.Mui-disabled': {
                  borderColor: '#e0e0e0',
                  color: '#bdbdbd',
                  backgroundColor: '#fafafa',
                },
                // Additional styling when handler is missing
                ...(!onClosePackage && {
                  opacity: 0.6,
                  cursor: 'not-allowed',
                }),
              }}
              startIcon={!canClosePackage && row.status === 'Completed' ? <LockIcon sx={{ fontSize: '16px !important' }} /> : undefined}
            >
              {t('close_package') || 'Close Package'}
            </Button>
          </span>
        </Tooltip>
      )}
    </TableCell>
  );
};

// Memoize component to prevent unnecessary re-renders
// Custom comparison function to check only relevant props
export const ClosePackageCell = React.memo(ClosePackageCellComponent, (prevProps, nextProps) => {
  // Return true if props are equal (skip re-render)
  // Return false if props changed (re-render needed)
  
  if (!prevProps.row && !nextProps.row) return true;
  if (!prevProps.row || !nextProps.row) return false;
  
  // Compare relevant row properties
  return (
    prevProps.row.id === nextProps.row.id &&
    prevProps.row.is_package_closed === nextProps.row.is_package_closed &&
    prevProps.row.can_close_package_invoice === nextProps.row.can_close_package_invoice &&
    prevProps.row.status === nextProps.row.status &&
    prevProps.onClosePackage === nextProps.onClosePackage &&
    prevProps.isClinicMismatch === nextProps.isClinicMismatch
  );
});

// Display name for debugging
ClosePackageCell.displayName = 'ClosePackageCell';
